import { useRef, useState } from "react";
import { FileDown, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { uuid, fmtDate } from "@/lib/format";
import { elementToPdf } from "@/lib/pdf";
import { CERT_KINDS, age, today } from "@/lib/clinic";
import { BigButton, Chip, Field, inputCls } from "@/components/ui-kit";
import { ReportHeader, ReportFooter } from "./ReportFrame";

export default function CertificateDialog({ patient, onClose }) {
  const { t, dispatch, view, staff, lang } = useApp();
  const [f, setF] = useState({ kind: "sick_leave", days: "3", from: today(), diagnosis: "", to_whom: "", text: "" });
  const ref = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => {
    const name = `${t(`cert_${f.kind}`)}${f.kind === "sick_leave" ? ` · ${f.days} ${t("hr_days")}` : ""}`;
    if (dispatch("record.upsert", { id: uuid(), patient_id: patient.id, patient_name: patient.name, kind: f.kind === "referral" ? "referral" : "certificate", name, result: f.diagnosis || f.to_whom, date: f.from, next_date: "", note: f.text, cert: f, by: staff.name })) { toast.success(t("saved")); onClose(); }
  };
  const body = f.kind === "sick_leave" ? t("cert_sick_text", { name: patient.name, days: f.days, from: fmtDate(f.from, lang, false) })
    : f.kind === "fitness" ? t("cert_fit_text", { name: patient.name }) : t("cert_ref_text", { name: patient.name, to: f.to_whom || "…" });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full p-0 bg-[#F0F0F0]" data-testid="cert-dialog">
        <div className="max-w-3xl mx-auto w-full p-4 space-y-4">
          <DialogTitle className="font-heading text-2xl">{t("certificates")} · {patient.name}</DialogTitle>
          <div className="flex gap-1.5">{CERT_KINDS.map((k) => <Chip key={k} active={f.kind === k} onClick={() => setF({ ...f, kind: k })} data-testid={`cert-kind-${k}`}>{t(`cert_${k}`)}</Chip>)}</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <Field label={t("hr_date")}><input type="date" dir="ltr" value={f.from} onChange={set("from")} className={inputCls} data-testid="cert-date-input" /></Field>
            {f.kind === "sick_leave" && <Field label={t("hr_days")}><input dir="ltr" inputMode="numeric" value={f.days} onChange={set("days")} className={inputCls} data-testid="cert-days-input" /></Field>}
            {f.kind === "referral" && <Field label={t("refer_to")}><input value={f.to_whom} onChange={set("to_whom")} className={inputCls} data-testid="cert-to-input" /></Field>}
            <Field label={t("diagnosis")}><input value={f.diagnosis} onChange={set("diagnosis")} className={inputCls} data-testid="cert-dx-input" /></Field>
            <Field label={t("hr_notes")}><input value={f.text} onChange={set("text")} className={inputCls} data-testid="cert-text-input" /></Field>
          </div>
          <div ref={ref} className="rounded border border-[#CCCCCC] bg-white p-6 text-[12px] text-[#222] leading-6 pdf-page" data-testid="cert-preview">
            <ReportHeader title={t(`cert_${f.kind}`)} patient={patient} date={f.from} />
            <p>{body}</p>
            {f.diagnosis && <p><b>{t("diagnosis")}:</b> {f.diagnosis}</p>}
            {f.text && <p>{f.text}</p>}
            <p className="text-xs text-[#666] mt-2">{patient.file_no}{patient.birth_date && ` · ${age(patient.birth_date)} ${t("years_short")}`}{patient.id_number && ` · ${patient.id_number}`}</p>
            <ReportFooter doctorName={staff.name} doctorId={staff.id} />
          </div>
          <div className="flex gap-2">
            <BigButton variant="soft" onClick={onClose} data-testid="cert-close-btn">{t("back")}</BigButton>
            <BigButton variant="soft" onClick={() => elementToPdf(ref.current, `${f.kind}-${patient.name}.pdf`)} data-testid="cert-pdf-btn"><FileDown className="w-5 h-5" />PDF</BigButton>
            <BigButton onClick={save} className="flex-1" data-testid="cert-save-btn"><Save className="w-5 h-5" />{t("save")}</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
